import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "./AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { MagicWand, ArrowRight } from "iconoir-react";

const priorityStyles = {
  high: "bg-intl/15 text-intl",
  medium: "bg-yellow-100 text-yellow-900",
  low: "bg-muted text-muted-foreground",
};

export default function Recommendations() {
  const { id } = useParams();
  const [recs, setRecs] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixes, setFixes] = useState({});

  useEffect(() => {
    let mounted = true;
    api.get(`/audits/${id}/recommendations`).then((r) => {
      if (mounted) {
        setRecs(r.data);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, [id]);

  const generate = async (rec) => {
    setBusyId(rec.id);
    try {
      const res = await api.post("/fixes/generate", { recommendation_id: rec.id });
      setFixes((f) => ({ ...f, [rec.id]: res.data }));
      toast.success("Fix generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusyId(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text || "");
    toast.success("Copied to clipboard");
  };

  return (
    <AppShell
      title="Recommendations"
      subtitle="// what to fix next"
      actions={
        <Link to={`/audits/${id}`} data-testid="back-to-audit-button" className="px-4 py-2 border border-border font-app text-xs uppercase tracking-widest font-bold hover:bg-card transition-colors">
          ← Back to audit
        </Link>
      }
    >
      <div data-testid="recommendations-page">
        {loading ? (
          <div className="space-y-4" data-testid="recommendations-skeleton">
            {[0, 1, 2].map((i) => (
              <div key={i} className="border border-border bg-card p-6 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-6 w-1/2" />
                <Skeleton className="h-12 w-full" />
              </div>
            ))}
          </div>
        ) : recs.length === 0 ? (
          <div className="border border-dashed border-border p-12 text-center bg-card">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// nothing pending</div>
            <h2 className="mt-2 font-marketing text-3xl">You're in great shape.</h2>
            <p className="mt-3 text-muted-foreground">We didn't surface any prioritised fixes for this audit.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {recs.map((r) => (
              <div key={r.id} className="border border-border bg-card" data-testid={`rec-${r.id}`}>
                <div className="p-6 grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-12 md:col-span-2 flex md:flex-col gap-2 items-start">
                    <span className={`inline-block px-2.5 py-1 font-mono text-xs uppercase tracking-widest ${priorityStyles[r.priority] || priorityStyles.medium}`}>{r.priority}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.category}</span>
                  </div>
                  <div className="col-span-12 md:col-span-7">
                    <h3 className="font-app text-xl">{r.title}</h3>
                    <p className="mt-2 text-muted-foreground leading-relaxed">{r.business_explanation}</p>
                    {r.technical_finding && (
                      <div className="mt-3 font-mono text-xs text-muted-foreground">// {r.technical_finding}</div>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-3 flex md:justify-end">
                    <button
                      onClick={() => generate(r)}
                      disabled={busyId === r.id}
                      data-testid={`generate-fix-${r.id}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors disabled:opacity-60"
                    >
                      <MagicWand width={14} height={14} />
                      {busyId === r.id ? "Generating…" : (fixes[r.id] ? "Regenerate" : "Generate fix")}
                    </button>
                  </div>
                </div>

                {fixes[r.id] && (
                  <div className="border-t border-border" data-testid={`fix-output-${r.id}`}>
                    <div className="flex items-center justify-between px-6 py-3 bg-obsidian text-bone">
                      <div className="font-mono text-xs uppercase tracking-widest">// {fixes[r.id].fix_type} · {fixes[r.id].title}</div>
                      <button
                        onClick={() => copy(fixes[r.id].content)}
                        data-testid={`copy-fix-${r.id}`}
                        className="font-mono text-xs uppercase tracking-widest text-intl hover:text-bone"
                      >
                        Copy
                      </button>
                    </div>
                    <pre className="p-6 terminal text-sm whitespace-pre-wrap overflow-x-auto max-h-[500px]">{fixes[r.id].content}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
